// Recordings and dates and Webinars worked out from their rows and the app's clock: the recordings list
// grouped by product, each item's chip, the editor's chapters, key messages, homework and tags, the
// cohort cards with their session rows, and each webinar's state and counts. Pure: no network and no
// React, so tests/e2e/admin-content.spec.js imports it as it is. Every date and time is UK time.
import { uk, shortDate, time, duration } from '../../lib/format.js'
import { chapterTime, parseStamp } from '../../lib/timestamp.js'
import { parseVimeo } from '../../free/vimeo.js'
import { vimeoLinkFrom, firstName, sessionTitle } from '../../products/mentorship/model.js'
import { COPY } from './copy.js'
import { GROUPS } from '../mastermind/words.js'
import { isSlug, isListName, listNameFromSlug, groupName, registerLink } from '../../free/register.js'

export { vimeoLinkFrom, firstName, sessionTitle }
export { listNameFromSlug, groupName, registerLink }

// The product types whose recordings and sessions this screen lists. A client space's live in its
// own space (guard_content), and free products have none.
const RECORDED = ['mastermind', 'live_cohort', 'on_demand', 'masterclass']
const SESSIONED = ['mastermind', 'live_cohort']

const ms = t => (t ? Date.parse(t) : NaN)
const byNum = (a, b) => (a ?? 0) - (b ?? 0)

// A product the hub holds: moved in (live's POYT and Accelerator aren't in phase 1, the critic's
// missed 2), and of a type with recordings
export const inHub = p => Boolean(p && p.moved_in && p.access_kind === 'paid' && RECORDED.includes(p.type))

// A product made in Add a product or by Duplicate (catalogue.created_by; the product-types plan, 13 Oct):
// a new on-demand course's modules are listed to take their recordings, with Add module after them.
// Every product that came with a move has none, so Property Cashflow For Life's list is as it was.
export const madeInAdmin = p => Boolean(p?.created_by)

// ---- Chips ----------------------------------------------------------------------------------------

// Live once switched on (is_published). A recording that fills a session is Tonight on its day before
// it starts and After the session before that day; anything else not on is a Draft (the critic's
// missed 9: chips come from is_published, tied to a session only where the recording belongs to one).
export function chipFor({ published, startsAt, nowMs }) {
  if (published) return 'live'
  const t = ms(startsAt)
  if (Number.isFinite(t) && t > nowMs) return uk(t).day === uk(nowMs).day ? 'tonight' : 'after'
  return 'draft'
}

// ---- The recordings list -----------------------------------------------------------------------------

const index = rows => new Map((rows || []).map(r => [r.id, r]))

// An item's name in the list: the week or module (with Property Cashflow For Life's part), a
// mastermind's group, else the recording's own title, else (a masterclass's video still to add) the
// product's own name
function itemName({ product, module, recording, session, cohort }) {
  if (product.type === 'mastermind') return cohort?.name || recording?.title || session?.title || ''
  if (module) return recording?.part ? COPY.partName(module.label, recording.part) : module.label
  return recording?.title || session?.title || product.name || ''
}

// The groups the list shows: 1 per product the hub holds, in the products' order, then 1 per open
// 1-2-1 space (owner and admin only: the reads give support nothing). Each item is
//   { kind: 'recording' | 'session' | 'video' | 'module' | 'client', key, name, date, sub, chip, product,
//     recording, session, module, cohort, content, clientSession, note, space, client }
// A 'session' item is a session with no recording yet (POYT's Week 1, an Accelerator group's
// session): saving it makes the recording. A 'video' item is a masterclass with no recording yet, and
// a 'module' item a module of an on-demand course made in admin with no recording yet (the product-types
// plan, 13 Oct): saving either makes the recording, with no session. Such a course's group is listed
// even with nothing in it, with addModule, the label of its Add module button. A mastermind's sessions run a year ahead, so of those still
// to come only each group's next 1 is listed (Adam, 13 Oct), with every 1 that has started or run;
// the rest are on Cohorts and dates. keepSession, the session named in the address (Today's links),
// is always listed.
export function buildGroups(data, nowMs, { keepSession = null } = {}) {
  const {
    products = [], cohorts = [], modules = [], sessions = [], recordings = [], content = [],
    spaces = [], clientSessions = [], notes = [], people = [],
  } = data
  const mods = index(modules)
  const sess = index(sessions)
  const cos = index(cohorts)
  const contentBy = new Map(content.map(c => [c.recording_id, c]))
  const groups = []

  for (const product of [...products].filter(inHub).sort((a, b) => byNum(a.sort_order, b.sort_order))) {
    const recs = recordings.filter(r => r.product_id === product.id)
    const items = recs.map(recording => {
      const session = recording.session_id ? sess.get(recording.session_id) || null : null
      const module = recording.module_id ? mods.get(recording.module_id) || null : null
      const cohort = cos.get(recording.cohort_id || session?.cohort_id) || null
      return { kind: 'recording', key: `r:${recording.id}`, product, recording, session, module, cohort, content: contentBy.get(recording.id) || null }
    })
    if (SESSIONED.includes(product.type)) {
      const waiting = sessions.filter(s => s.product_id === product.id && s.status !== 'cancelled').filter(session => {
        const filled = recs.some(r => r.session_id === session.id
          || (product.type === 'live_cohort' && session.module_id && r.module_id === session.module_id))
        return !filled && !(product.type === 'live_cohort' && !session.module_id)
      })
      // a mastermind group's next session still to come (Tonight's, until it starts)
      const next = new Map()
      if (product.type === 'mastermind') {
        for (const s of waiting) {
          const t = ms(s.starts_at)
          if (!(t > nowMs)) continue
          const was = next.get(s.cohort_id ?? null)
          if (!was || t < ms(was.starts_at)) next.set(s.cohort_id ?? null, s)
        }
      }
      for (const session of waiting) {
        if (product.type === 'mastermind' && ms(session.starts_at) > nowMs && next.get(session.cohort_id ?? null) !== session
          && session.id !== keepSession) continue
        items.push({
          kind: 'session', key: `s:${session.id}`, product, recording: null, session,
          module: session.module_id ? mods.get(session.module_id) || null : null, cohort: cos.get(session.cohort_id) || null, content: null,
        })
      }
    }
    // a masterclass with no recording yet: its video to add (the video at /masterclass has its own, so
    // its items are as they were)
    if (product.type === 'masterclass' && !recs.length) {
      items.push({ kind: 'video', key: `v:${product.id}`, product, recording: null, session: null, module: null, cohort: null, content: null })
    }
    // an on-demand course made in admin: each module with no recording yet takes its first
    let addModule = null
    if (product.type === 'on_demand' && madeInAdmin(product)) {
      const filled = new Set(recs.map(r => r.module_id).filter(Boolean))
      const mine = modules.filter(m => m.product_id === product.id)
      for (const module of mine.filter(m => !filled.has(m.id)).sort((a, b) => byNum(a.sort_order, b.sort_order))) {
        items.push({ kind: 'module', key: `m:${module.id}`, product, recording: null, session: null, module, cohort: null, content: null })
      }
      addModule = { label: COPY.addModule(Math.max(0, ...mine.map(m => m.number ?? 0)) + 1) }
    }
    if (product.type === 'mastermind') {
      // newest session first, as the board lists the Accelerator's groups
      items.sort((a, b) => (ms(b.session?.starts_at) || 0) - (ms(a.session?.starts_at) || 0))
    } else {
      items.sort((a, b) => byNum(a.module?.sort_order, b.module?.sort_order)
        || byNum(a.recording?.sort_order, b.recording?.sort_order)
        || (ms(a.session?.starts_at) || 0) - (ms(b.session?.starts_at) || 0))
    }
    for (const it of items) {
      it.name = itemName(it)
      it.date = it.session?.starts_at ? shortDate(it.session.starts_at) : null
      it.sub = it.kind === 'video' ? COPY.videoSub
        : it.date || (it.module && it.recording && it.recording.title !== it.name ? it.recording.title : '')
      it.chip = chipFor({ published: it.recording?.is_published, startsAt: it.session?.starts_at, nowMs })
    }
    if (items.length || addModule) groups.push({ key: product.id, name: product.name, product, items, ...(addModule ? { addModule } : {}) })
  }

  // the 1-2-1: a group per open space, its sessions newest first, with the reduced editor
  const peopleBy = index(people)
  const notesBy = new Map(notes.map(n => [n.session_id, n]))
  const clientProduct = products.find(p => p.type === 'client_space') || null
  for (const space of spaces.filter(s => !s.ended_at)) {
    const client = peopleBy.get(space.client_person_id) || null
    const product = products.find(p => p.id === space.product_id) || clientProduct
    const items = clientSessions.filter(s => s.space_id === space.id).sort((a, b) => b.number - a.number).map(cs => {
      const note = notesBy.get(cs.id) || null
      return {
        kind: 'client', key: `c:${cs.id}`, product, space, client, clientSession: cs, note,
        name: sessionTitle(cs), date: cs.starts_at ? shortDate(cs.starts_at) : null,
        sub: cs.starts_at ? shortDate(cs.starts_at) : '',
        chip: chipFor({ published: Boolean(note?.shown_at), startsAt: cs.starts_at, nowMs }),
      }
    })
    if (items.length) {
      groups.push({ key: space.id, name: COPY.clientGroup(product?.name || '', client?.full_name || ''), product, items, client: true })
    }
  }
  return groups
}

// The item asked for in the address (?recording=, ?session=, ?client_session=, and ?item= for a video or
// a module still to add), else the first Tonight: either opens even inside a product folded away in the
// list, and shows its group open for that visit
export function askedItem(groups, { recording, session, clientSession, item } = {}) {
  const items = groups.flatMap(g => g.items)
  return (recording && items.find(i => i.recording?.id === recording))
    || (session && items.find(i => i.session?.id === session && i.kind !== 'client'))
    || (clientSession && items.find(i => i.clientSession?.id === clientSession))
    || (item && items.find(i => i.key === item && (i.kind === 'video' || i.kind === 'module')))
    || items.find(i => i.chip === 'tonight')
    || null
}

// Which item opens first: the 1 asked for or Tonight's (askedItem), else, in the products not folded
// away (folded: the groups' keys), the session that ran most recently, the recording Adam's about to add
// (Adam, 13 Oct), else their first item. Without nowMs there's no "most recently", as before.
export function pickItem(groups, asked = {}, { folded = null, nowMs = NaN } = {}) {
  const first = askedItem(groups, asked)
  if (first) return first
  const open = groups.filter(g => !folded?.has(g.key)).flatMap(g => g.items)
  let ran = null
  if (Number.isFinite(nowMs)) {
    for (const it of open) {
      const t = it.kind === 'client' ? NaN : ms(it.session?.starts_at)
      if (t <= nowMs && (!ran || t > ms(ran.session.starts_at))) ran = it
    }
  }
  return ran || open[0] || groups.flatMap(g => g.items)[0] || null
}

// Where an item opens from, kept in the address as it's picked, so a reload or Back opens it again
export function addressFor(item) {
  if (item.kind === 'client') return `/admin/recordings?client_session=${item.clientSession.id}`
  if (item.recording) return `/admin/recordings?recording=${item.recording.id}`
  if (!item.session) return `/admin/recordings?item=${encodeURIComponent(item.key)}`
  return `/admin/recordings?session=${item.session.id}`
}

// Whether an item's recording takes its own thumbnail (decision 47: the courses' and the Accelerator's,
// never the video's or a 1-2-1's), for the editor's block and the list's mark
export const takesThumbnail = item => item.kind !== 'client' && item.product?.type !== 'masterclass'

// Who a recording's email goes to, for "Email <who> when it goes live" and the Live line: a
// mastermind group's recording goes to its group, a recording of a course released by intake to
// the session's intake (Profit On Your Terms, and The Funding Engine since Adam's 10 Oct, as
// AdminRecordings.dc.html says it: "Live. Cohort 3 can watch it now."), and anything else to everyone
// with the product (the critic's missed 9)
export function audienceFor(item) {
  if (item.kind === 'client') return firstName(item.client) || ''
  const { product, recording, session, cohort } = item
  if (product.type === 'mastermind' && (recording ? recording.cohort_id : session?.cohort_id) && cohort) return cohort.name
  if (product.release_basis === 'own_intake' && session?.cohort_id && cohort) return cohort.name
  return COPY.everyoneWith(product.name)
}

// The editor's heading: "Week 4, Wed 30 Sep", "Module 1, Motivation", or the name alone
export function headingFor(item) {
  if (item.date) return `${item.name}, ${item.date}`
  if (item.sub) return `${item.name}, ${item.sub}`
  return item.name
}

// ---- Vimeo links and lengths ---------------------------------------------------------------------------

// What a pasted link or embed code is: { state: 'empty' | 'found' | 'bad', url, id, hash }
export function readLink(input) {
  const text = String(input || '').trim()
  if (!text) return { state: 'empty', url: null, id: null, hash: null }
  const url = vimeoLinkFrom(text)
  const v = url ? parseVimeo(url) : null
  if (!v) return { state: 'bad', url: null, id: null, hash: null }
  return { state: 'found', url, id: v.id, hash: v.hash || null }
}

// Whether 2 links are the same video (the same id and privacy hash)
export function sameVideo(a, b) {
  const x = a ? parseVimeo(vimeoLinkFrom(a) || '') : null
  const y = b ? parseVimeo(vimeoLinkFrom(b) || '') : null
  return Boolean(x && y && x.id === y.id && (x.hash || null) === (y.hash || null))
}

// A typed length as seconds: 1:49:22, 49:22, 1h49m, or a number of minutes (109)
export function parseLength(text) {
  const s = String(text || '').trim().toLowerCase().replace(/\s+/g, '')
  if (!s) return null
  if (/^\d{1,4}$/.test(s)) return Number(s) > 0 ? Number(s) * 60 : null
  const v = parseStamp(/^\d+$/.test(s) ? `${s}s` : s)
  return v && v > 0 ? v : null
}
export const lengthText = seconds => (seconds ? chapterTime(seconds) : '')

// ---- Chapters, 1 a line ---------------------------------------------------------------------------------

const CHAPTER_LINE = /^(\d{1,2}:\d{2}(?::\d{2})?)\s+(.+)$/

// The board's preview: each line as { time, title, ok }
export function readChapterLines(text) {
  return String(text || '').split('\n').map(l => l.trim()).filter(Boolean).map(line => {
    const m = CHAPTER_LINE.exec(line)
    const seconds = m ? parseStamp(m[1]) : null
    return m && seconds != null ? { time: m[1], title: m[2].trim(), ok: true, seconds } : { time: '?', title: line, ok: false, seconds: null }
  })
}

// The chapters to save, in start order, from the lines: { lines, bad, duplicate, chapters }. A
// chapter's no is its place (1, 2, 3) where every chapter so far was numbered; where they carry
// labels (Property Cashflow For Life's '3.6'), a chapter keeps the label of the one it replaces at
// the same time, or the same title.
export function chaptersFromText(text, existing = []) {
  const lines = readChapterLines(text)
  const bad = lines.filter(l => !l.ok).length
  const ok = lines.filter(l => l.ok).sort((a, b) => a.seconds - b.seconds)
  let duplicate = null
  for (let i = 1; i < ok.length; i++) if (ok[i].seconds === ok[i - 1].seconds) { duplicate = chapterTime(ok[i].seconds); break }
  const numbered = (existing || []).every(c => c.no == null || typeof c.no === 'number')
  const chapters = ok.map((l, i) => {
    let no = i + 1
    if (!numbered) {
      const same = existing.find(c => c.start_seconds === l.seconds) || existing.find(c => c.title === l.title)
      no = same ? same.no : null
    }
    return { no, title: l.title, start_seconds: l.seconds }
  })
  return { lines, bad, duplicate, chapters }
}

export const chaptersToText = chapters => (chapters || [])
  .slice().sort((a, b) => a.start_seconds - b.start_seconds)
  .map(c => `${chapterTime(c.start_seconds)} ${c.title}`).join('\n')

// Whether the chapters to save are the ones there now (so an unchanged box writes nothing new)
export const sameChapters = (a, b) => JSON.stringify((a || []).map(c => [c.start_seconds, c.title, c.no ?? null]))
  === JSON.stringify((b || []).slice().sort((x, y) => x.start_seconds - y.start_seconds).map(c => [c.start_seconds, c.title, c.no ?? null]))

// ---- Key messages, 1 a line ---------------------------------------------------------------------------

export const keyMessageLine = k => [k?.head, k?.body].filter(s => s && String(s).trim()).join(' ').trim()
export const keyMessagesToText = list => (list || []).map(keyMessageLine).filter(Boolean).join('\n')

// Each line as { head, body }: a line that's unchanged keeps its message as it is; a new or changed
// line's first sentence is the head and the rest its body
export function keyMessagesFromText(text, existing = []) {
  return String(text || '').split('\n').map(l => l.trim()).filter(Boolean).map(line => {
    const same = (existing || []).find(k => keyMessageLine(k) === line)
    if (same) return same
    const m = /^(.+?[.?!])\s+(\S.*)$/.exec(line)
    return m ? { head: m[1], body: m[2] } : { head: line }
  })
}

// ---- Tags -------------------------------------------------------------------------------------------------

export function parseTags(text) {
  const seen = new Set()
  const out = []
  for (const raw of String(text || '').split(',')) {
    const t = raw.trim()
    if (!t || seen.has(t.toLowerCase())) continue
    seen.add(t.toLowerCase())
    out.push(t)
  }
  return out
}
export const tagsText = tags => (tags || []).join(', ')

// ---- Homework, 1 a line -----------------------------------------------------------------------------------

// The homework a recording's box shows: its own items, and its module's items that belong to no
// recording (Property Cashflow For Life's Module 3, shared by 3A, 3B and 3C). A recording with no
// module has no homework (homework_items.module_id is required).
export function homeworkShown({ recordingId, moduleId }, items) {
  if (!moduleId) return []
  return (items || []).filter(h => !h.removed_at && h.module_id === moduleId && (h.recording_id == null || h.recording_id === recordingId))
    .sort((a, b) => byNum(a.sort_order, b.sort_order) || String(a.id).localeCompare(String(b.id)))
}
export const homeworkText = shown => shown.map(h => h.body).join('\n')

// What saving the box changes, keeping every line's id so members' ticks and certificates survive
// (data-model 4.2): a line that's still there keeps its item (matched by its words, then by its
// place), a line no longer there is removed softly, a new line is a new item that takes the group
// heading and kind of the line above it (Week 6's commitments stay commitments).
//   { updates: [{ id, patch }], inserts: [rows], removals: [ids], tooLong }
export function homeworkPlan(text, shown, { productId, moduleId, recordingId }) {
  const lines = String(text || '').split('\n').map(l => l.trim()).filter(Boolean)
  const tooLong = lines.some(l => l.length > 500)
  const used = new Set()
  const match = lines.map(() => null)
  lines.forEach((l, i) => {
    const h = shown.find(x => !used.has(x.id) && x.body === l)
    if (h) { match[i] = h; used.add(h.id) }
  })
  lines.forEach((l, i) => {
    if (match[i]) return
    const h = shown[i]
    if (h && !used.has(h.id) && !lines.includes(h.body)) { match[i] = h; used.add(h.id) }
  })
  const base = shown.length ? Math.min(...shown.map(h => h.sort_order ?? 0)) : 1
  // new lines join the recording's own items, or its module's shared ones where that's all there is
  const forRecording = shown.length && shown.every(h => h.recording_id == null) ? null : recordingId
  const updates = []
  const inserts = []
  let group = null
  lines.forEach((l, i) => {
    const order = base + i
    const h = match[i]
    if (h) {
      const patch = {}
      if (h.body !== l) patch.body = l
      if ((h.sort_order ?? 0) !== order) patch.sort_order = order
      if (Object.keys(patch).length) updates.push({ id: h.id, patch })
      group = { kind: h.kind || 'homework', group_heading: h.group_heading ?? null }
    } else {
      const below = match.slice(i + 1).find(Boolean)
      const g = group || (below ? { kind: below.kind || 'homework', group_heading: below.group_heading ?? null } : { kind: 'homework', group_heading: null })
      inserts.push({ product_id: productId, module_id: moduleId, recording_id: forRecording, body: l, sort_order: order, kind: g.kind, group_heading: g.group_heading })
      group = g
    }
  })
  const removals = shown.filter(h => !used.has(h.id)).map(h => h.id)
  return { updates, inserts, removals, tooLong }
}

// ---- Cohorts and dates ------------------------------------------------------------------------------------

// A session row's state: Held once it has started (a register can be taken), Set a date, Zoom link
// set, No Zoom link yet, or Cancelled
export function sessionState(s, nowMs) {
  if (s.status === 'cancelled') return 'cancelled'
  if (!s.starts_at) return 'setDate'
  if (s.status === 'held' || ms(s.starts_at) <= nowMs) return 'held'
  return s.zoom_join_url ? 'zoomSet' : 'noZoom'
}

const lowerFirst = s => (s ? s[0].toLowerCase() + s.slice(1) : s)

// The next intake's name and number: "Cohort 4" from the product's cohort_naming ("Cohort {n}")
export function nextCohort(product, cohorts) {
  const mine = cohorts.filter(c => c.product_id === product.id)
  const n = Math.max(0, ...mine.map(c => c.number ?? 0), mine.length) + 1
  const naming = product.cohort_naming || product.settings?.cohort_naming
  const pattern = naming?.pattern || (product.type === 'mastermind' ? 'Group {n}' : 'Cohort {n}')
  const name = pattern.replace('{n}', String(n))
  return { number: n, name, label: `Add ${lowerFirst(name)}` }
}

// The cards: The Funding Engine's and other live courses' intakes (newest first, only those with
// sessions), then the masterminds' groups. Each is { key, product, cohort, pattern, rows, link,
// add: { kind: 'cohort' | 'session', label } | null }; each row { session, label, date, time, state }.
// A product made with nothing on it yet (the product-types plan, 13 Oct) gets a foot-only card, { key,
// product, foot: true, cohort: null, rows: [], add }: a live course with weeks but no intake with sessions,
// to add its first (add: cohort), and a mastermind with no group (add: null), so its groups' foot draws.
export function buildCohortCards({ products = [], cohorts = [], sessions = [], modules = [], links = [] }, nowMs) {
  const mods = index(modules)
  const linkBy = new Map(links.map(l => [l.cohort_id, l]))
  const hub = products.filter(p => p.moved_in && SESSIONED.includes(p.type))
  const ordered = [
    ...hub.filter(p => p.type === 'live_cohort').sort((a, b) => byNum(a.sort_order, b.sort_order)),
    ...hub.filter(p => p.type === 'mastermind').sort((a, b) => byNum(a.sort_order, b.sort_order)),
  ]
  const cards = []
  for (const product of ordered) {
    const mine = cohorts.filter(c => c.product_id === product.id)
    const withSessions = mine.filter(c => sessions.some(s => s.cohort_id === c.id))
    const weeks = modules.some(m => m.product_id === product.id && m.number != null)
    if (product.type === 'live_cohort' && !withSessions.length && weeks) {
      cards.push({ key: `${product.id}:first`, product, foot: true, cohort: null, pattern: '', rows: [], link: null,
        add: { kind: 'cohort', label: nextCohort(product, cohorts).label } })
      continue
    }
    if (product.type === 'mastermind' && !mine.length) {
      const every = sessions.some(s => s.product_id === product.id && !s.cohort_id)
      if (!every) {
        cards.push({ key: `${product.id}:groups`, product, foot: true, cohort: null, pattern: '', rows: [], link: null, add: null })
        continue
      }
    }
    const list = product.type === 'mastermind'
      ? mine.slice().sort((a, b) => byNum(a.sort_order, b.sort_order) || byNum(a.number, b.number))
      : withSessions.slice().sort((a, b) => byNum(b.number, a.number) || byNum(b.sort_order, a.sort_order))
    list.forEach((cohort, i) => {
      const rows = sessions.filter(s => s.cohort_id === cohort.id)
        .sort((a, b) => (ms(a.starts_at) || Infinity) - (ms(b.starts_at) || Infinity) || byNum(a.number, b.number))
        .map(session => {
          const module = session.module_id ? mods.get(session.module_id) : null
          return {
            session, module,
            label: product.type === 'mastermind' ? COPY.session : (module?.label || session.title),
            date: session.starts_at ? shortDate(session.starts_at) : COPY.noDate,
            time: session.starts_at ? time(session.starts_at) : '',
            state: sessionState(session, nowMs),
          }
        })
      let add = null
      if (product.type === 'mastermind') add = { kind: 'session', label: COPY.addSession }
      else if (i === 0) add = { kind: 'cohort', label: nextCohort(product, cohorts).label }
      cards.push({
        key: cohort.id, product, cohort, pattern: cohort.schedule_label || '', rows, add,
        link: product.type === 'mastermind' ? (linkBy.get(cohort.id) || { cohort_id: cohort.id, whatsapp_invite_url: null }) : null,
      })
    })
    // a mastermind's sessions for every group (cohort_id null, data-model 4.2), on a card of their own
    // once there's 1 (the mastermind build; undrawn)
    if (product.type === 'mastermind') {
      const every = sessions.filter(s => s.product_id === product.id && !s.cohort_id)
        .sort((a, b) => (ms(a.starts_at) || Infinity) - (ms(b.starts_at) || Infinity))
      if (every.length) {
        cards.push({
          key: `${product.id}:every`, product, cohort: { id: null, name: GROUPS.every }, pattern: GROUPS.everyPattern, every: true,
          rows: every.map(session => ({
            session, module: null, label: COPY.session,
            date: session.starts_at ? shortDate(session.starts_at) : COPY.noDate,
            time: session.starts_at ? time(session.starts_at) : '',
            state: sessionState(session, nowMs),
          })),
          add: { kind: 'every', label: COPY.addSession }, link: null,
        })
      }
    }
  }
  return cards
}

// The sessions a new intake gets: 1 per numbered module, as The Funding Engine's 6 weeks, each with no
// date yet ("Set a date") and the product's usual length
export function sessionsForCohort(product, cohortId, modules) {
  return modules.filter(m => m.product_id === product.id && m.number != null)
    .sort((a, b) => byNum(a.sort_order, b.sort_order))
    .map(m => ({
      product_id: product.id, cohort_id: cohortId, module_id: m.id, number: m.number, title: m.label,
      duration_minutes: product.default_session_minutes || 90, status: 'scheduled',
    }))
}

// The lengths a session can be, as the boards write them
export function lengthOptions(current) {
  const values = [30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240]
  if (current && !values.includes(current)) values.push(current)
  return values.sort((a, b) => a - b).map(v => ({ value: String(v), label: duration(v * 60) }))
}

// ---- Webinars -----------------------------------------------------------------------------------------------

export const webinarEnds = w => ms(w.starts_at) + (w.duration_minutes || 0) * 60000
export const isPast = (w, nowMs) => webinarEnds(w) <= nowMs

export function webinarState(w, nowMs) {
  if (isPast(w, nowMs)) return w.replay_url ? (w.replay_up ? 'replayUp' : 'replayDown') : 'heldNoReplay'
  return w.shown ? 'shown' : 'hidden'
}

// Whether it's shown while still to come: when the database writes "New free webinar" in the bell
export const shownAhead = (w, nowMs) => Boolean(w?.shown) && !isPast(w, nowMs)

// The list's count beside each webinar: watched for a replay (the hub's own count), and for 1 still to
// come, Shown or Not shown (the board's registered count was Zoom's, and the hub never reads Zoom)
export function webinarCount(w, stat, nowMs) {
  if (isPast(w, nowMs)) return COPY.watched(stat?.replay_watched ?? 0)
  return w.shown ? COPY.shownCount : COPY.notShown
}

// Coming up (soonest first) and Replays (latest first)
export function webinarGroups(webinars, nowMs) {
  const up = webinars.filter(w => !isPast(w, nowMs)).sort((a, b) => ms(a.starts_at) - ms(b.starts_at))
  const past = webinars.filter(w => isPast(w, nowMs)).sort((a, b) => ms(b.starts_at) - ms(a.starts_at))
  return [
    { key: 'up', name: COPY.comingUp, items: up },
    { key: 'past', name: COPY.replays, items: past },
  ].filter(g => g.items.length)
}

export const webinarWhen = w => `${shortDate(w.starts_at)}, ${time(w.starts_at)}`
export const webinarWhenLong = w => `${webinarWhen(w)}, ${duration((w.duration_minutes || 0) * 60)}`

// The tile: the hub's own count of the replay watched, or Not yet until there's a replay to watch
export function webinarTiles(w, stat, nowMs) {
  const C = COPY.counts
  const counted = isPast(w, nowMs) && Boolean(w.replay_url)
  return [
    { key: 'watched', label: C.watchedReplay, value: counted ? String(stat?.replay_watched ?? 0) : C.notYet, note: counted ? C.inHub : C.onceUp },
  ]
}

// Whether a save switches a webinar on while it's still to come (added that way, or shown or moved
// ahead now when it wasn't both before): the write the database's bell_new_webinar can ring for, so the
// screen asks hub.bell_will_ring first
export const switchesOnAhead = ({ before, after, nowMs }) => shownAhead(after, nowMs) && !(before && shownAhead(before, nowMs))

// The product a new webinar goes on, as the database picks it (private.assign_webinar_defaults): the
// webinar product, Free webinars, a live 1 first
export function webinarProductOf(products) {
  return (products || []).filter(p => p.type === 'webinar')
    .sort((a, b) => Number(b.state === 'live') - Number(a.state === 'live') || byNum(a.sort_order, b.sort_order) || String(a.id).localeCompare(String(b.id)))[0] || null
}

// The line once a webinar is saved: switched on while still to come, it's in every account's bell when
// the database said so before the write (rings, from hub.bell_will_ring: never for 1 shown before, so
// switching it off and on again doesn't claim it); added, it says whether members see it yet
export function webinarSavedLine({ before, after, nowMs, rings = false }) {
  if (!before) {
    if (shownAhead(after, nowMs)) return rings ? COPY.webinarAddedShown : COPY.webinarAddedShownNoBell
    return after.shown ? COPY.webinarAddedReplay : COPY.webinarAddedHidden
  }
  if (switchesOnAhead({ before, after, nowMs })) return rings ? COPY.webinarSavedShown : COPY.webinarSavedShownNoBell
  return COPY.webinarSaved
}

// Zoom's own registration link, as the database takes it (webinars_registration_url_check, Adam, 5 Oct,
// stage 6's batch, question 2): https://, then zoom.us or a zoom.us address such as us06web.zoom.us,
// then / and the rest. Case-sensitive, as the database's check is.
export const ZOOM_REGISTRATION = /^https:\/\/([a-z0-9-]+\.)*zoom\.us\//

// A typed registration link as it's saved: trimmed, with its scheme and address in lower case (they
// mean the same either way, and the database's check is lower case), or null when there's none
export function registrationLink(text) {
  const s = String(text || '').trim()
  if (!s) return null
  const m = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)(.*)$/is.exec(s)
  return m ? m[1].toLowerCase() + m[2] : s
}

// Whether a webinar shown while still to come has no registration link, so members can't register:
// the line under "Show it to every account" (question 2). form: { shown, past, registration }
export const showsWithoutRegistration = ({ shown, past, registration }) => Boolean(shown) && !past && !registrationLink(registration)

// What a webinar can sell: the paid courses and groups (no Property School, which the hub holds no
// owners for: Adam's answer 10, C17), then Nothing. Products not moved in stay: their owners are known
// from where they live now (legacy ownership hides the call to action from them).
export function sellOptions(products) {
  return products.filter(p => p.access_kind === 'paid' && ['mastermind', 'live_cohort', 'on_demand'].includes(p.type))
    .sort((a, b) => byNum(a.sort_order, b.sort_order))
    .map(p => ({ value: p.id, label: p.name, product: p }))
    .concat([{ value: null, label: COPY.nothing, product: null }])
}

// What's typed about a webinar (the database's checks: a title of 1 to 200 characters, a description of
// up to 2,000, a date and a start, and a registration link that's Zoom's own, or none), checked before
// it's saved: the words to show, or null. form: { title, description, day, at, registration }
export function webinarDetailsProblem(form) {
  const title = (form.title || '').trim()
  if (!title) return COPY.titleNeeded
  if (title.length > 200) return COPY.titleLong
  if ((form.description || '').trim().length > 2000) return COPY.descriptionLong
  if (!form.day || !form.at) return COPY.whenNeeded
  const reg = registrationLink(form.registration)
  if (reg && !ZOOM_REGISTRATION.test(reg)) return COPY.registrationBad
  return null
}

// ---- A webinar's thumbnail (Adam, 9 Oct) ----------------------------------------------------------

// What a thumbnail may be: the covers bucket's types and size (sign-upload's cover start), and the size
// the browser redraws it to before it's uploaded, so a big PNG doesn't cost members megabytes
export const THUMBNAIL_TYPES = ['image/jpeg', 'image/png', 'image/webp']
export const THUMBNAIL_MAX_BYTES = 5 * 1024 * 1024
export const THUMBNAIL_SIZE = { width: 1280, height: 720 }

// A picked file checked before it's read: the words to show, or null
export function thumbnailFileProblem(file) {
  if (!file || !THUMBNAIL_TYPES.includes(String(file.type || '').toLowerCase())) return COPY.thumbnailBadType
  if (file.size > THUMBNAIL_MAX_BYTES) return COPY.thumbnailTooBig
  return null
}

// The size it's redrawn at: the whole picture, at most 1280 by 720, never made bigger
export function fitWithin(width, height, max = THUMBNAIL_SIZE) {
  const scale = Math.min(1, max.width / width, max.height / height)
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

// 16:9, give or take a pixel or 2 of rounding (1280 by 720, 1920 by 1080, 1366 by 768)
export const isSixteenNine = (width, height) => width > 0 && height > 0 && Math.abs(width / height - 16 / 9) <= 0.01

// A webinar's call to action and replay checked before they're saved: the words to show, or null
export function webinarProblem(form) {
  const label = (form.cta_label || '').trim()
  const url = (form.cta_url || '').trim()
  if (Boolean(label) !== Boolean(url)) return COPY.ctaBoth
  if (url && !/^https:\/\//i.test(url)) return COPY.ctaHttps
  if ((form.replay || '').trim() && readLink(form.replay).state !== 'found') return COPY.replayBad
  return null
}

// ---- A replay's register page (docs/replay-register-spec.md 3.1 and 5) ------------------------------------

// The link as it's typed: lower case, a space as a hyphen
export const typedSlug = v => String(v ?? '').toLowerCase().replace(/\s+/g, '-')

// Whether the switch may go on now, from what's on the screen (unsaved): the words to show, or null.
// On needs a link, a name in MailerLite, a replay link and the replay up, as the database has it.
export function registerOnProblem({ slug, name, hasReplay, replayUp }) {
  const s = String(slug ?? '').trim()
  const n = String(name ?? '').trim()
  if (!s) return COPY.rrNeedsSlug
  if (!isSlug(s)) return COPY.rrSlugBad
  if (!n) return COPY.rrNeedsName
  if (!isListName(n)) return COPY.rrNameBad
  if (!hasReplay || !replayUp) return COPY.rrNeedsReplay
  return null
}

// The register settings checked on Save: the words to show, or null. A page that's on keeps its link, its
// name and its replay up, so taking the replay down or its link away asks for the page to go off first.
export function registerProblem({ slug, name, on, hasReplay, replayUp }) {
  const s = String(slug ?? '').trim()
  const n = String(name ?? '')
  if (s && !isSlug(s)) return COPY.rrSlugBad
  if (n.trim() && !isListName(n)) return COPY.rrNameBad
  if (on) {
    if (!s) return COPY.rrNeedsSlug
    if (!n.trim()) return COPY.rrNeedsName
    if (!hasReplay || !replayUp) return COPY.rrTakeOffFirst
  }
  return null
}

// The register fields to save, only those that differ from the saved webinar, so a save that doesn't
// touch them writes what it always wrote
export function registerPatch(saved, { slug, name, on }) {
  const next = { register_slug: String(slug ?? '').trim() || null, register_list_name: String(name ?? '').trim() || null, register_on: Boolean(on) }
  const before = { register_slug: saved?.register_slug ?? null, register_list_name: saved?.register_list_name ?? null, register_on: Boolean(saved?.register_on) }
  const out = {}
  for (const k of Object.keys(next)) if (next[k] !== before[k]) out[k] = next[k]
  return out
}

// What a person registered owns, as the list writes it: "The Funding Engine", "Profit On Your Terms, place
// secured" (hub.replay_registrations_for's owns: { name, status, since })
export function ownsWords(owns) {
  return (Array.isArray(owns) ? owns : []).filter(o => o?.name).map(o => {
    const st = o.status && o.status !== 'active' ? COPY.rrOwnsStatus[o.status] || o.status : null
    return st ? `${o.name}, ${st}` : o.name
  })
}

// The registrations list's CSV (admin/ui/csv.js keeps a cell a spreadsheet would run as a formula as text)
export const REGISTRATIONS_CSV = [
  { key: 'first_name', label: 'First name' },
  { key: 'email', label: 'Email' },
  { key: 'registered_at', label: 'Registered', format: 'datetime' },
  { key: 'last_registered_at', label: 'Last registered', format: 'datetime' },
  { key: 'times_registered', label: 'Times' },
  { key: 'source', label: 'Source' },
  { label: 'Owns', value: r => ownsWords(r.owns).join('; ') },
  { key: 'made_account', label: 'New free account', format: 'yesno' },
  { key: 'is_test', label: 'Test', format: 'yesno' },
]

// The property strategy tool's completions (Stage 2, docs/property-strategy-spec.md S2.9): the hand-off's words. Only a
// real failure reads "Not sent", so the weekly check finds just those.
export const LEAD_WORDS = {
  sent: 'Sent',
  not_sent: 'Not sent',
  waiting: 'Waiting',
  sent_before: 'Sent before',
  paid: 'Not for emails: paid access',
  revoked: 'Not for emails: access revoked',
  no_consent: 'Not for emails: no consent',
}
export const leadWords = status => LEAD_WORDS[status] || LEAD_WORDS.waiting

// The completions list's CSV (admin/ui/csv.js keeps a cell a spreadsheet would run as a formula as text)
export const STRATEGY_CSV = [
  { key: 'first_name', label: 'First name' },
  { key: 'email', label: 'Email' },
  { key: 'created_at', label: 'Completed', format: 'datetime' },
  { key: 'source', label: 'Source' },
  { key: 'strategy_name', label: 'Strategy' },
  { label: 'Sent', value: r => leadWords(r.lead_status) },
  { key: 'made_account', label: 'New free account', format: 'yesno' },
  { label: 'Test', value: r => (r.person?.is_test ? 'Yes' : 'No') },
]
